import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, Mail, UserPlus, Trash2, Shield, User as UserIcon, Clock, CheckCircle, XCircle, AlertCircle, Crown, UserCog, Plus, CreditCard, Smartphone } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import type { CurrentPage } from '../App';
import type { User } from '@supabase/supabase-js';
import type { OrganizationMember, TeamInvitation, Organization, UserRole } from '../types/team';
import { ROLE_METADATA } from '../types/team';
import {
  getUserOrganization,
  getOrganizationMembers,
  getOrganizationInvitations,
  inviteTeamMember,
  removeMember,
  revokeInvitation,
  isSuperAdmin,
  updateMemberRole
} from '../services/teamService';
import memberSlotService, { type MemberSlotInfo, type MemberSlotPurchase } from '../services/memberSlotService';
import { PLAN_LIMITS } from '../types/team';
import { supabase } from '../lib/supabaseClient';

interface TeamManagementPageProps {
  onNavigate: (page: CurrentPage) => void;
  currentUser: User | null;
}

const TeamManagementPage: React.FC<TeamManagementPageProps> = ({ onNavigate, currentUser }) => {
  // Scroll to top
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Member slot management
  const [slotInfo, setSlotInfo] = useState<MemberSlotInfo | null>(null);
  const [pendingPurchases, setPendingPurchases] = useState<MemberSlotPurchase[]>([]);
  const [purchasedSlotSubs, setPurchasedSlotSubs] = useState<string[]>([]);
  const [showSlotPurchaseModal, setShowSlotPurchaseModal] = useState(false);
  const [slotsToRequest, setSlotsToRequest] = useState(1);
  const [selectedSlotTier, setSelectedSlotTier] = useState<string | null>(null);
  const [isRequestingSlots, setIsRequestingSlots] = useState(false);
  
  // Invitation form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('team_member');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  
  // Role change modal
  const [roleChangeModal, setRoleChangeModal] = useState<{
    memberId: string;
    memberEmail: string;
    currentRole: UserRole;
  } | null>(null);
  const [roleChangeConfirm, setRoleChangeConfirm] = useState<{
    memberId: string;
    memberEmail: string;
    currentRole: UserRole;
    newRole: UserRole;
  } | null>(null);
  const [roleChangeSuccess, setRoleChangeSuccess] = useState<string | null>(null);
  
  // Delete member confirmation modal
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    memberId: string;
    memberEmail: string;
    memberName: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load data
  useEffect(() => {
    loadTeamData();
    
    // Subscribe to changes in team_invitations for real-time updates
    const subscription = supabase
      .channel('team_invitations_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'team_invitations'
        }, 
        (payload) => {
          console.log('[TeamManagement] Real-time update:', payload.eventType);
          // Reload invitations when any change occurs (INSERT, UPDATE, DELETE)
          getOrganizationInvitations().then(setInvitations).catch(console.error);
          // Also reload members in case invitation was accepted
          getOrganizationMembers().then(setMembers).catch(console.error);
        }
      )
      .subscribe();
    
    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadTeamData = async () => {
    try {
      setIsLoading(true);
      const [org, teamMembers, teamInvites, adminStatus] = await Promise.all([
        getUserOrganization(),
        getOrganizationMembers(),
        getOrganizationInvitations(),
        isSuperAdmin()
      ]);

      setOrganization(org);
      setMembers(teamMembers);
      setInvitations(teamInvites);
      setIsAdmin(adminStatus);

      if (org) {
        const [membersData, invitesData, slotsData, pendingPurchasesData] = await Promise.all([
          getOrganizationMembers(),
          getOrganizationInvitations(),
          memberSlotService.getMemberSlotInfo(org.id),
          memberSlotService.getPendingPurchases(org.id)
        ]);
        
        setMembers(membersData);
        setInvitations(invitesData);
        setSlotInfo(slotsData);
        setPendingPurchases(pendingPurchasesData);
        
        // Load purchased slot subscriptions
        const { data: orgData } = await supabase
          .from('organizations')
          .select('purchased_slot_subscriptions')
          .eq('id', org.id)
          .single();
        
        setPurchasedSlotSubs(orgData?.purchased_slot_subscriptions || []);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(false);

    if (!inviteEmail.trim()) {
      setInviteError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setInviteError('Please enter a valid email address');
      return;
    }

    try {
      setIsSendingInvite(true);
      await inviteTeamMember(inviteEmail, inviteRole);
      setInviteSuccess(true);
      setInviteEmail('');
      setInviteRole('team_member');
      
      // Reload invitations
      const updatedInvites = await getOrganizationInvitations();
      setInvitations(updatedInvites);

      // Clear success message after 5 seconds
      setTimeout(() => setInviteSuccess(false), 5000);
    } catch (error: any) {
      setInviteError(error.message || 'Failed to send invitation');
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberEmail: string, memberName: string) => {
    // Show confirmation modal instead of simple confirm
    setDeleteConfirmModal({ memberId, memberEmail, memberName });
  };
  
  const handleConfirmDelete = async () => {
    if (!deleteConfirmModal) return;
    
    setIsDeleting(true);
    try {
      await removeMember(deleteConfirmModal.memberId);
      
      // Reload members and organization data
      const [updatedMembers, updatedOrg] = await Promise.all([
        getOrganizationMembers(),
        getUserOrganization()
      ]);
      setMembers(updatedMembers);
      if (updatedOrg) setOrganization(updatedOrg);
      
      // Close modal
      setDeleteConfirmModal(null);
    } catch (error: any) {
      alert(`Failed to remove member: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string, email: string) => {
    if (!confirm(`Are you sure you want to revoke the invitation to ${email}?`)) {
      return;
    }

    try {
      await revokeInvitation(invitationId);
      
      // Reload invitations
      const updatedInvites = await getOrganizationInvitations();
      setInvitations(updatedInvites);
    } catch (error: any) {
      alert(`Failed to revoke invitation: ${error.message}`);
    }
  };

  const handleOpenRoleChange = (memberId: string, currentRole: UserRole, memberEmail: string) => {
    setRoleChangeModal({ memberId, memberEmail, currentRole });
  };

  const handleRoleSelect = (newRole: UserRole) => {
    if (!roleChangeModal) return;
    
    const { memberId, memberEmail, currentRole } = roleChangeModal;
    
    // Check if role is the same
    if (newRole === currentRole) {
      setRoleChangeModal(null);
      setRoleChangeSuccess('No changes made - member already has this role.');
      setTimeout(() => setRoleChangeSuccess(null), 3000);
      return;
    }

    // Show confirmation dialog
    setRoleChangeConfirm({ memberId, memberEmail, currentRole, newRole });
  };

  const handleConfirmRoleChange = async () => {
    if (!roleChangeConfirm) return;
    
    const { memberId, memberEmail, newRole } = roleChangeConfirm;

    try {
      await updateMemberRole(memberId, newRole);
      
      // Reload all team data including organization to update space count
      // team_admin doesn't count toward limit, so changing to/from this role affects available spaces
      const [updatedOrg, updatedMembers] = await Promise.all([
        getUserOrganization(),
        getOrganizationMembers()
      ]);
      
      setOrganization(updatedOrg);
      setMembers(updatedMembers);
      setRoleChangeModal(null);
      setRoleChangeConfirm(null);
      
      // Show success message
      setRoleChangeSuccess(`Successfully updated ${memberEmail}'s role to ${newRole.replace('_', ' ')}!`);
      setTimeout(() => setRoleChangeSuccess(null), 5000);
    } catch (error: any) {
      alert(`Failed to update role: ${error.message}`);
      setRoleChangeConfirm(null);
    }
  };

  const handleCancelRoleChange = () => {
    setRoleChangeConfirm(null);
  };

  const handleRequestSlots = async () => {
    if (!organization || slotsToRequest < 1) return;

    try {
      setIsRequestingSlots(true);
      await memberSlotService.requestAdditionalSlots(organization.id, slotsToRequest);
      
      // Reload slot info and pending purchases
      const [slots, pending] = await Promise.all([
        memberSlotService.getMemberSlotInfo(organization.id),
        memberSlotService.getPendingPurchases(organization.id)
      ]);
      setSlotInfo(slots);
      setPendingPurchases(pending);
      
      setShowSlotPurchaseModal(false);
      setSlotsToRequest(1);
      alert(`Successfully requested ${slotsToRequest} additional slot(s). Payment required to activate.`);
    } catch (error: any) {
      alert(`Failed to request slots: ${error.message}`);
    } finally {
      setIsRequestingSlots(false);
    }
  };

  const handlePurchaseSlotViaIAP = async (productId: string, slotCount: number) => {
    if (!organization) return;

    try {
      setIsRequestingSlots(true);
      const result = await memberSlotService.purchaseSlotViaIAP(organization.id, productId);
      
      if (result.success) {
        // Reload slot info
        const slots = await memberSlotService.getMemberSlotInfo(organization.id);
        setSlotInfo(slots);
        
        // Reload members to reflect new capacity
        const updatedMembers = await getOrganizationMembers();
        setMembers(updatedMembers);
        
        // Update purchased subscriptions list
        const { data: orgData } = await supabase
          .from('organizations')
          .select('purchased_slot_subscriptions')
          .eq('id', organization.id)
          .single();
        
        setPurchasedSlotSubs(orgData?.purchased_slot_subscriptions || []);
        setShowSlotPurchaseModal(false);
        setSelectedSlotTier(null);
        alert(`Successfully purchased ${slotCount} additional team member slot${slotCount > 1 ? 's' : ''}! ${slotCount > 1 ? 'They are' : 'It is'} now active.`);
      } else {
        alert(`Purchase failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert(`Failed to purchase slot: ${error.message}`);
    } finally {
      setIsRequestingSlots(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mb-4"></div>
          <p className="text-slate-600 font-medium">Loading team data...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 text-orange-500" size={48} />
          <h2 className="text-xl font-bold text-slate-900 mb-2">No Organization Found</h2>
          <p className="text-slate-600 mb-6">
            You need a Business or Small Team account to access team management.
          </p>
          <button
            onClick={() => onNavigate({ page: 'start' })}
            className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const planLimits = PLAN_LIMITS[organization.subscription_plan];
  
  // Use slotInfo if available, otherwise fall back to old calculation
  const currentMemberCount = slotInfo?.currentMembers || members.filter(m => ROLE_METADATA[m.role]?.countsTowardLimit).length + 1;
  const totalSlots = slotInfo?.totalSlots || planLimits.maxMembers;
  const slotsRemaining = slotInfo?.availableSlots || (planLimits.maxMembers - currentMemberCount);
  const pendingInvites = invitations.filter(inv => inv.status === 'pending');
  const canPurchaseSlots = organization.subscription_plan === 'business' || organization.subscription_plan === 'team';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
      {/* Header */}
      <div className="max-w-6xl mx-auto w-full mb-6">
        <button
          onClick={() => onNavigate({ page: 'start' })}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium mb-4 transition-colors group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 sm:p-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 bg-clip-text text-transparent mb-2">
                Team Management
              </h1>
              <p className="text-slate-600 text-lg">{organization.name}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-sm shadow-md">
                {planLimits.displayName} Plan
              </span>
              {isAdmin && (
                <span className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-300 text-amber-800 font-semibold text-xs flex items-center gap-1.5">
                  <Crown size={14} />
                  Super Admin
                </span>
              )}
            </div>
          </div>

          {/* Member Count Progress */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-700 font-semibold">Team Members</span>
              <span className="text-slate-900 font-bold">
                {currentMemberCount} / {totalSlots}
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2.5">
              <div
                className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${(currentMemberCount / totalSlots) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-slate-600">
                {slotsRemaining > 0 ? (
                  <>{slotsRemaining} slot{slotsRemaining !== 1 ? 's' : ''} remaining</>
                ) : (
                  <span className="text-orange-600 font-semibold">Team is full</span>
                )}
                {slotInfo && slotInfo.purchasedSlots > 0 && (
                  <span className="ml-2 text-green-600 font-semibold">
                    (+{slotInfo.purchasedSlots} purchased)
                  </span>
                )}
              </p>
              {isAdmin && canPurchaseSlots && (
                <button
                  onClick={() => setShowSlotPurchaseModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-all"
                >
                  <Plus size={14} />
                  Add Slots
                </button>
              )}
            </div>
            {pendingPurchases.length > 0 && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 font-semibold flex items-center gap-1">
                  <Clock size={12} />
                  {pendingPurchases.length} pending purchase{pendingPurchases.length !== 1 ? 's' : ''} awaiting payment
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Members */}
        <div className="space-y-6">
          {/* Team Members List */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-xl">
                <Users size={24} className="text-indigo-700" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Team Members</h2>
            </div>

            <div className="space-y-3">
              {members.map((member) => {
                const isCurrentUser = member.user_id === currentUser?.id;
                const isOwner = member.user_id === organization.owner_id;
                const fullName = `${member.user_metadata?.first_name || ''} ${member.user_metadata?.last_name || ''}`.trim();

                return (
                  <div
                    key={member.id}
                    className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200 hover:border-indigo-300 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-lg ${
                          member.role === 'super_admin'
                            ? 'bg-gradient-to-r from-amber-100 to-orange-100'
                            : member.role === 'team_leader'
                            ? 'bg-gradient-to-r from-purple-100 to-pink-100'
                            : member.role === 'team_admin'
                            ? 'bg-gradient-to-r from-cyan-100 to-blue-100'
                            : 'bg-gradient-to-r from-blue-100 to-indigo-100'
                        }`}>
                          {member.role === 'super_admin' ? (
                            <Shield size={20} className="text-amber-700" />
                          ) : (
                            <UserIcon size={20} className={
                              member.role === 'team_leader' ? 'text-purple-700' :
                              member.role === 'team_admin' ? 'text-cyan-700' :
                              'text-indigo-700'
                            } />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900">
                              {fullName || 'Team Member'}
                            </p>
                            {isCurrentUser && (
                              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded">
                                You
                              </span>
                            )}
                            {isOwner && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded flex items-center gap-1">
                                <Crown size={12} />
                                Owner
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 truncate">{member.email}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {ROLE_METADATA[member.role].displayName} •{' '}
                            {member.user_metadata?.assessor_type || 'Team Member'}
                          </p>
                        </div>
                      </div>

                      {/* Actions (only for super admins, and not for self) */}
                      {isAdmin && !isCurrentUser && !isOwner && (
                        <div className="flex items-center gap-2 ml-2">
                          <button
                            onClick={() => handleOpenRoleChange(member.id, member.role, member.email || '')}
                            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                            title="Change role"
                          >
                            <UserCog size={18} className="text-slate-600" />
                          </button>
                          <button
                            onClick={() => {
                              const firstName = member.user_metadata?.first_name || '';
                              const lastName = member.user_metadata?.last_name || '';
                              const fullName = [firstName, lastName].filter(Boolean).join(' ') || member.email || 'Team Member';
                              handleRemoveMember(member.id, member.email || '', fullName);
                            }}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors group"
                            title="Remove member"
                          >
                            <Trash2 size={18} className="text-slate-600 group-hover:text-red-600" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {members.length === 0 && (
                <div className="text-center py-8">
                  <Users size={48} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No team members yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Invite & Invitations */}
        <div className="space-y-6">
          {/* Invite Form */}
          {isAdmin && (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl">
                  <UserPlus size={24} className="text-green-700" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Invite Member</h2>
              </div>

              {slotsRemaining > 0 ? (
                <form onSubmit={handleInviteMember} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="colleague@example.com"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      disabled={isSendingInvite}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as UserRole)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white"
                      disabled={isSendingInvite}
                    >
                      <option value="team_member">{ROLE_METADATA.team_member.displayName}</option>
                      <option value="team_leader">{ROLE_METADATA.team_leader.displayName}</option>
                      <option value="team_admin">{ROLE_METADATA.team_admin.displayName}</option>
                    </select>
                    
                    {/* Role Description */}
                    <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <p className="text-xs text-slate-600 leading-relaxed">
                        <span className="font-semibold text-slate-900">{ROLE_METADATA[inviteRole].displayName}:</span>{' '}
                        {ROLE_METADATA[inviteRole].description}
                      </p>
                      {!ROLE_METADATA[inviteRole].countsTowardLimit && (
                        <p className="text-xs text-green-700 font-semibold mt-2 flex items-center gap-1">
                          <CheckCircle size={14} />
                          Doesn't count toward team member limit
                        </p>
                      )}
                    </div>
                  </div>

                  {inviteError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <XCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{inviteError}</p>
                    </div>
                  )}

                  {inviteSuccess && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-green-700">
                        Invitation sent successfully! They'll receive an email shortly.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSendingInvite || !inviteEmail.trim()}
                    className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    {isSendingInvite ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail size={20} />
                        Send Invitation
                      </>
                    )}
                  </button>

                  <p className="text-xs text-slate-500 text-center">
                    Invited members get free access under your plan
                  </p>
                </form>
              ) : (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                  <AlertCircle size={24} className="mx-auto text-orange-600 mb-2" />
                  <p className="text-sm text-orange-800 font-semibold mb-1">Team Limit Reached</p>
                  <p className="text-xs text-orange-700">
                    Your {planLimits.displayName} plan allows up to {planLimits.maxMembers} members.
                    Remove a member to invite someone new.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Pending Invitations */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-xl">
                <Clock size={24} className="text-amber-700" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900">Pending Invitations</h2>
                <p className="text-sm text-slate-600">{pendingInvites.length} waiting</p>
              </div>
            </div>

            <div className="space-y-3">
              {pendingInvites.map((invitation) => {
                const daysLeft = Math.ceil(
                  (new Date(invitation.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                );

                return (
                  <div
                    key={invitation.id}
                    className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-200"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{invitation.invited_email}</p>
                        <p className="text-xs text-slate-600 mt-1">
                          Sent {new Date(invitation.created_at).toLocaleDateString()} •{' '}
                          {daysLeft > 0 ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left` : 'Expired'}
                        </p>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={() => handleRevokeInvitation(invitation.id, invitation.invited_email)}
                          className="px-3 py-1.5 text-xs bg-white hover:bg-red-50 text-slate-700 hover:text-red-700 border border-slate-300 hover:border-red-300 rounded-lg font-semibold transition-all"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {pendingInvites.length === 0 && (
                <div className="text-center py-8">
                  <Mail size={48} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No pending invitations</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Role Change Modal */}
      {roleChangeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Change Role</h3>
            <p className="text-sm text-slate-600 mb-6">
              Select a new role for <span className="font-semibold">{roleChangeModal.memberEmail}</span>
            </p>

            <div className="space-y-3 mb-6">
              {(['team_leader', 'team_member', 'team_admin'] as UserRole[]).map((role) => {
                const metadata = ROLE_METADATA[role];
                const isCurrentRole = role === roleChangeModal.currentRole;
                
                return (
                  <button
                    key={role}
                    onClick={() => handleRoleSelect(role)}
                    disabled={isCurrentRole}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                      isCurrentRole
                        ? 'border-indigo-500 bg-indigo-50 cursor-default'
                        : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-semibold text-slate-900">{metadata.displayName}</span>
                      {isCurrentRole && (
                        <span className="text-xs bg-indigo-600 text-white px-2 py-1 rounded-full font-semibold">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{metadata.description}</p>
                    {!metadata.countsTowardLimit && (
                      <p className="text-xs text-green-700 font-semibold mt-2 flex items-center gap-1">
                        <CheckCircle size={12} />
                        Doesn't count toward limit
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setRoleChangeModal(null)}
              className="w-full px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Role Change Confirmation Dialog */}
      {roleChangeConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="text-amber-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Confirm Role Change</h3>
            </div>
            
            <p className="text-slate-700 mb-6">
              Are you sure you want to change <span className="font-semibold">{roleChangeConfirm.memberEmail}</span>'s role from{' '}
              <span className="font-semibold text-indigo-600">{roleChangeConfirm.currentRole.replace('_', ' ')}</span> to{' '}
              <span className="font-semibold text-green-600">{roleChangeConfirm.newRole.replace('_', ' ')}</span>?
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleCancelRoleChange}
                className="flex-1 px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRoleChange}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-all shadow-lg"
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Member Confirmation Modal */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 animate-scale-in">
            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="text-red-600" size={32} />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-slate-900 text-center mb-4">
              Delete Team Member
            </h3>

            {/* Warning Message */}
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-red-900">
                  <p className="font-bold mb-2">⚠️ Warning: This action cannot be undone!</p>
                  <p className="mb-2">
                    Deleting <span className="font-semibold">{deleteConfirmModal.memberName}</span> ({deleteConfirmModal.memberEmail}) will permanently delete:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>All floor plans and surveys created by this user</li>
                    <li>All uploaded images and documents</li>
                    <li>All property data and assessments</li>
                    <li>Their user account (if not in other teams)</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-slate-600 text-center mb-6">
              Are you absolutely sure you want to proceed?
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmModal(null)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-slate-200 hover:bg-slate-300 disabled:bg-slate-100 text-slate-700 disabled:text-slate-400 rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-red-400 disabled:to-red-500 text-white rounded-lg font-semibold transition-all shadow-lg flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete Permanently
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Info Message Banner */}
      {roleChangeSuccess && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-in-right">
          <div className="bg-white rounded-xl shadow-2xl border-2 border-green-500 p-4 max-w-md">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <p className="text-slate-800 font-medium">{roleChangeSuccess}</p>
            </div>
          </div>
        </div>
      )}

      {/* Request Additional Slot Modal - Multi-Tier */}
      {showSlotPurchaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 border-2 border-purple-200">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl mr-4">
                <Plus className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">
                  Add Team Member Slots
                </h3>
                <p className="text-sm text-slate-600">Choose a subscription tier to expand your team</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-purple-200 rounded-xl p-4">
                <p className="text-sm text-purple-800 mb-2">
                  💳 <strong>Apple In-App Purchase</strong>
                </p>
                <p className="text-xs text-purple-700">
                  Purchase additional team member slots directly through the iOS app. Each tier is a separate subscription - you can purchase all three if needed.
                </p>
              </div>

              {/* Slot Tier Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.values(memberSlotService.SLOT_TIERS).map((tier) => {
                  const isPurchased = purchasedSlotSubs.includes(tier.productId);
                  const isSelected = selectedSlotTier === tier.productId;
                  
                  return (
                    <button
                      key={tier.productId}
                      onClick={() => !isPurchased && setSelectedSlotTier(tier.productId)}
                      disabled={isPurchased || isRequestingSlots}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        isPurchased
                          ? 'bg-slate-100 border-slate-300 cursor-not-allowed opacity-60'
                          : isSelected
                          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-500 ring-2 ring-green-300'
                          : 'bg-white border-slate-200 hover:border-green-400'
                      }`}
                    >
                      {isPurchased && (
                        <div className="absolute top-2 right-2 px-2 py-1 bg-slate-600 text-white text-xs font-bold rounded">
                          Active
                        </div>
                      )}
                      <div className="text-center">
                        <p className="text-lg font-bold text-slate-800 mb-1">{tier.displayName}</p>
                        <p className="text-sm text-slate-600 mb-3">{tier.description}</p>
                        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg py-2 px-3">
                          <p className="text-2xl font-bold">£{tier.price}</p>
                          <p className="text-xs opacity-90">per month</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {!Capacitor.isNativePlatform() && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <Smartphone className="text-amber-600 flex-shrink-0" size={20} />
                  <p className="text-sm text-amber-800">
                    <strong>iOS App Required:</strong> Slot purchases are only available on iOS devices through Apple In-App Purchase.
                  </p>
                </div>
              )}

              {/* Subscription Information (Apple Guideline 3.1.2) */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-slate-600 text-center">
                  💳 <strong>Auto-Renewable Subscription</strong> • Payment charged to Apple ID at confirmation. Renews unless cancelled 24h before period ends.
                </p>
              </div>

              {/* Privacy Policy & Terms */}
              <div className="flex gap-2 justify-center text-xs">
                <a 
                  href="https://noorcad.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-600 hover:text-slate-900 underline"
                >
                  Privacy Policy
                </a>
                <span className="text-slate-400">•</span>
                <a 
                  href="https://noorcad.com/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-600 hover:text-slate-900 underline"
                >
                  Terms of Use
                </a>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSlotPurchaseModal(false);
                  setSelectedSlotTier(null);
                }}
                disabled={isRequestingSlots}
                className="flex-1 px-6 py-3 bg-slate-200 hover:bg-slate-300 disabled:bg-slate-100 text-slate-700 font-semibold rounded-xl transition-all"
              >
                Cancel
              </button>
              {Capacitor.isNativePlatform() && (
                <button
                  onClick={() => {
                    if (selectedSlotTier) {
                      const tier = Object.values(memberSlotService.SLOT_TIERS).find(t => t.productId === selectedSlotTier);
                      if (tier) {
                        handlePurchaseSlotViaIAP(tier.productId, tier.slots);
                      }
                    }
                  }}
                  disabled={!selectedSlotTier || isRequestingSlots}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-bold rounded-xl shadow-lg transition-all disabled:cursor-not-allowed"
                >
                  {isRequestingSlots ? 'Processing...' : selectedSlotTier ? `Purchase ${Object.values(memberSlotService.SLOT_TIERS).find(t => t.productId === selectedSlotTier)?.displayName}` : 'Select a Tier'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementPage;
